import React from 'react';
import PropTypes from 'prop-types';
import { FocusScope } from '@react-aria/focus';
import { useEditor, useEditingCond, useNode } from '@splashthat/splash-cms-engine';
import Close from 'common/icons/CloseForm';

import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_HOVER_COLOR,
} from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';

import { Modal as ManagedModal } from 'common/components/TouchpointEditor/contexts/ModalManager';

import { ModalContainer, ModalWrapper, CloseButtonContainer, ModalGlobalStyles } from './Modal.styles';

const editorSelector = (state) => ({ editing: state.editing });
const preventModalClickFromClosing = (e) => e.stopPropagation();

export default function Modal({ descendants: { modalContent, closeButton }, hovered, selected, id, children }) {
    const { theme } = useTouchpointData();
    const editor = useEditor(editorSelector);
    const node = useNode();
    const customCssClassName = getCustomCssClassName(node);
    const onCloseCallbackIfOnEditorMode = useEditingCond(() => editor.actions.unselectAllNodes());
    const isEndUser = !editor.editing;

    return (
        <ManagedModal modalId={id}>
            {({ isOpen, onResolve }) => {
                const shouldShowModal = isOpen || selected;

                const onClose = onCloseCallbackIfOnEditorMode || onResolve;

                if (!shouldShowModal) {
                    return null;
                }

                return (
                    <ModalContainer
                        className={`${id} cms-engine-modal`}
                        data-qa={`ModalContainer__${id}`}
                        onClick={onClose}
                    >
                        <ModalGlobalStyles isEditing={editor.editing} />
                        <FocusScope contain={isEndUser} restoreFocus autoFocus>
                            <ModalWrapper
                                data-qa={`${id}__modal-wrapper`}
                                $backgroundColor={theme.colors[modalContent.styles.backgroundColor]}
                                $padding={modalContent.styles.padding}
                                $gap={modalContent.styles.gap}
                                $mainDirection={modalContent.styles.alignment.mainDirection}
                                $perpendicularDirection={modalContent.styles.alignment.perpendicularDirection}
                                $direction={modalContent.styles.direction}
                                role="dialog"
                                aria-modal="true"
                                selected={selected}
                                hovered={hovered}
                                onMouseLeave={node.actions.onMouseLeave()}
                                onMouseMove={node.actions.onMouseMove()}
                                onClick={node.actions.onClick(preventModalClickFromClosing)}
                                className={customCssClassName}
                            >
                                <CloseButtonContainer
                                    $color={theme.colors[closeButton.styles.color]}
                                    $colorHover={theme.colors[closeButton.styles.colorHover]}
                                    onClick={onClose}
                                    data-qa={id}
                                    aria-label="Close"
                                >
                                    <Close />
                                </CloseButtonContainer>
                                {children}
                            </ModalWrapper>
                        </FocusScope>
                    </ModalContainer>
                );
            }}
        </ManagedModal>
    );
}

Modal.splash = {
    related: {
        settings: React.lazy(() => import('./Modal.tools')),
    },
    defaultProps: {
        descendants: {
            modalContent: {
                styles: {
                    backgroundColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    padding: {
                        top: 64,
                        bottom: null,
                        left: 48,
                        right: null,
                    },
                    gap: 32,
                    alignment: {
                        mainDirection: 'start',
                        perpendicularDirection: 'start',
                    },
                    direction: 'column',
                },
            },
            closeButton: {
                styles: {
                    color: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
                    colorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
                },
            },
        },
    },
};

Modal.propTypes = {
    descendants: PropTypes.object.isRequired,
    hovered: PropTypes.bool.isRequired,
    selected: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};
